"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { AD_CLIENT } from "~/lib/ads";

type AdBannerProps = {
  dataAdSlot: string;
  dataAdFormat?: string;
  dataFullWidthResponsive?: boolean;
  className?: string;
};

type AdsWindow = Window & { adsbygoogle?: unknown[] };

/**
 * Responsive AdSense unit for the App Router.
 * - Re-mounts on every route change so client-side navigation gets fresh ads.
 * - Waits until the slot has a width (AdSense throws on width 0).
 * - Collapses itself when AdSense reports no fill (see globals.css).
 * - Renders a labelled placeholder in development instead of requesting ads.
 */
function AdUnit({ dataAdSlot, dataAdFormat = "auto", dataFullWidthResponsive = true }: AdBannerProps) {
  const ref = useRef<HTMLModElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let done = false;
    const push = () => {
      if (done || el.offsetWidth === 0 || el.getAttribute("data-adsbygoogle-status")) return;
      done = true;
      try {
        const w = window as AdsWindow;
        (w.adsbygoogle = w.adsbygoogle ?? []).push({});
      } catch {
        /* ad blocked or script not ready */
      }
    };
    push();
    if (done) return;
    // slot not laid out yet (e.g. hidden tab / collapsed panel): wait for it to get a width
    const ro = new ResizeObserver(push);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <ins
      ref={ref}
      className="adsbygoogle"
      style={{ display: "block" }}
      data-ad-client={AD_CLIENT}
      data-ad-slot={dataAdSlot}
      data-ad-format={dataAdFormat}
      data-full-width-responsive={dataFullWidthResponsive.toString()}
    />
  );
}

export default function AdBanner(props: AdBannerProps) {
  const pathname = usePathname();

  if (process.env.NODE_ENV !== "production") {
    return (
      <div className={`ad-slot ${props.className ?? ""}`}>
        <div className="grid h-24 place-items-center rounded-lg border border-dashed border-deck-line font-mono text-xs text-deck-muted">
          Ad slot {props.dataAdSlot}
        </div>
      </div>
    );
  }

  return (
    <div className={`ad-slot ${props.className ?? ""}`}>
      <p className="ad-label mb-1 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-deck-muted/60">Advertisement</p>
      <AdUnit key={pathname} {...props} />
    </div>
  );
}
